import React from 'react';
import { Box, Typography } from '@mui/material';
import { useProfilePlan } from '../../contexts/ProfilePlanContext';
import PlanChip from '../PlanChip';

// Displays a profile name with a small plan badge (Basic/Advance/Pro) when available.
export default function ProfileWithPlanBadge({ profile, small = false, sx = {}, showBadge = true }) {
  const { getPlanDetails, loadProfileSubscription, planCode: contextPlanCode, planLabel: contextPlanLabel } = useProfilePlan();

  // Build a canonical profileKey that matches what backend checks expect.
  // Prefer explicit `profileKey`, then `fullLocationName` (accounts/.../locations/...),
  // then raw `locationId`, then account:location fallback, then business name.
  const profileKey = profile?.profileKey
    || profile?.fullLocationName
    || profile?.locationId
    || (profile?.accountData && profile?.locationId ? `${(profile.accountData.name || profile.accountData.accountId || '').toString().split('/').pop()}:${profile.locationId}` : null)
    || profile?.name || null;

  // Kick off a load if missing (non-blocking)
  React.useEffect(() => {
    if (!profileKey) return;

    // Try to load using the most authoritative key first (fullLocationName),
    // then fall back to the simple locationId if needed.
    const tryLoad = async () => {
      try {
        await loadProfileSubscription(profileKey);
      } catch (_) {
        // If profileKey was a plain locationId, attempt a namespaced form
        if (profile?.locationId && !String(profileKey).includes('/')) {
          try {
            await loadProfileSubscription(`locations/${profile.locationId}`);
          } catch (_) { /* ignore */ }
        }
      }
    };

    tryLoad();
  }, [profileKey, loadProfileSubscription, profile?.locationId]);

  const plan = getPlanDetails();

  const mapLabelToCode = (lbl) => {
    if (!lbl) return null;
    const s = String(lbl).toLowerCase();
    if (s.includes('basic')) return 'basic';
    if (s.includes('adv')) return 'advance';
    if (s.includes('pro') || s.includes('premium')) return 'pro';
    return null;
  };

  // Prefer authoritative context code, then derive from labels; ignore 'No Plan' label
  const derivedFromContext = contextPlanCode || mapLabelToCode(contextPlanLabel);
  const derivedFromService = (plan && plan.code) || mapLabelToCode(plan?.label);
  const derivedFromProfile = mapLabelToCode(profile?.planLabel || profile?.currentPlanLabel || profile?.plan);

  const canonicalPlanCode = derivedFromContext || derivedFromService || derivedFromProfile || null;
  const canonicalPlanLabel = canonicalPlanCode ? (plan?.label || contextPlanLabel || profile?.planLabel || canonicalPlanCode.charAt(0).toUpperCase() + canonicalPlanCode.slice(1)) : null;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, ...sx }}>
      <Typography component="span" variant={small ? 'body2' : 'body1'} sx={{ fontWeight: 600 }}>
        {profile?.name || profile?.profile_name || profileKey || 'Profile'}
      </Typography>
      {showBadge && canonicalPlanCode ? (
        <PlanChip plan={canonicalPlanCode} size={small ? 'small' : 'medium'} label={canonicalPlanLabel} />
      ) : null}
    </Box>
  );
}
